import { useCallback, useMemo } from "react"
import { useMutation, useQueryClient } from "react-query"
import { WEBHOOK } from "reducers/webhook/webhook-actions"
import ApiWebhook from "api/ApiWebhook"
import { mapWebhookToUpdate } from "../mapper/webhookMapper"
import useHandleApiError from "hooks/useHandleApiErrors"

export type WebhookSubmitTypes = "create" | "update" | "delete" | "updateIsPublished"

const useWebhookRequest = (submitType: WebhookSubmitTypes, onSuccess: () => void, defaultErrorMessage: string) => {
  const queryClient = useQueryClient()

  const successAction = useCallback(() => {
    onSuccess?.()
    queryClient.invalidateQueries(WEBHOOK)
  }, [onSuccess])

  const { getErrorMessageAndShowErrorToast } = useHandleApiError()

  const createWebhook = useMutation({
    mutationFn: async (data: App.IWebhook) => {
      return new ApiWebhook().create(data)
    },
    onSuccess: successAction,
    onError: (error) => {
      getErrorMessageAndShowErrorToast(error, defaultErrorMessage)
    }
  })

  const updateWebhook = useMutation({
    mutationFn: async (data: App.IWebhook) => {
      const mappedWebhook = mapWebhookToUpdate(data)

      return new ApiWebhook().update(mappedWebhook, data._id)
    },
    onSuccess: successAction,
    onError: (error) => {
      getErrorMessageAndShowErrorToast(error, defaultErrorMessage)
    }
  })

  const updateWebhookIsPublished = useMutation({
    mutationFn: async (data: App.IWebhook) => {
      return new ApiWebhook().updateIsPublished(data._id)
    },
    onSuccess: successAction,
    onError: (error) => {
      getErrorMessageAndShowErrorToast(error, defaultErrorMessage)
    }
  })

  const isLoading = useMemo(() => {
    return updateWebhookIsPublished.isLoading
  }, [updateWebhookIsPublished.isLoading])

  const onSubmit = useCallback(
    (data: App.IWebhook) => {
      switch (submitType) {
        case "create":
          createWebhook.mutate(data)
          break
        case "update":
          updateWebhook.mutate(data)
          break
        case "updateIsPublished":
          updateWebhookIsPublished.mutate(data)
          break
        default:
          break
      }
    },
    [submitType]
  )

  return {
    onSubmit,
    isLoading
  }
}

export default useWebhookRequest
